import { useCallback, useEffect, useRef, useState } from 'react';

export interface UseControlledProps<Value> {
  /**
   * Holds the component value when it's controlled.
   */
  controlledValue?: Value;
  /**
   * The default value when uncontrolled.
   */
  defaultValue?: Value;
  /**
   * The component name displayed in warnings.
   */
  name: string;
  /**
   * The name of the state variable displayed in warnings.
   * @default 'value'
   */
  state?: string;
}

const useControlledValue = <Value>(
  props: UseControlledProps<Value>,
): [undefined | Value, (newValue: Value) => void] => {
  const { controlledValue, defaultValue, name, state = 'value' } = props;

  const isControlled = controlledValue !== undefined;
  const { current: origIsControlled } = useRef(isControlled);

  const [value, setValue] = useState(defaultValue);
  const currentValue = isControlled ? controlledValue : value;

  const { current: origDefaultValue } = useRef(defaultValue);

  useEffect(() => {
    if (process.env.NODE_ENV === 'production' || isControlled === origIsControlled) {
      return;
    }

    console.warn(
      [
        `RBUI: A component is changing the ${isControlled ? '' : 'un'}controlled ${state} state of ${name} to be ${
          isControlled ? 'un' : ''
        }controlled.`,
        'Elements should not switch from uncontrolled to controlled (or vice versa).',
        `Decide between using a controlled or uncontrolled ${name} element for the lifetime of the component.`,
        'The nature of the state is determined during the first render.',
        "It's considered controlled if the value is not `undefined`.",
        'More info: https://fb.me/react-controlled-components',
      ].join('\n'),
    );
  }, [state, name, controlledValue, isControlled, origIsControlled]);

  useEffect(() => {
    if (process.env.NODE_ENV === 'production' || isControlled || Object.is(origDefaultValue, defaultValue)) {
      return;
    }

    console.warn(
      [
        `RBUI: A component is changing the default ${state} state of an uncontrolled ${name} after being initialized.`,
        `To suppress this warning opt to use a controlled ${name}.`,
      ].join('\n'),
    );
  }, [defaultValue, isControlled, name, origDefaultValue, state]);

  const setValueIfUncontrolled = useCallback(
    (newValue: Value) => {
      if (!isControlled) {
        setValue(newValue);
      }
    },
    [isControlled],
  );

  return [currentValue, setValueIfUncontrolled];
};

export default useControlledValue;
